/**
 * Job Scout API client.
 *
 * Read + review surface for what the Oubo Job Scout extension crawls. The
 * extension drives the run endpoints itself; this client covers what the admin
 * panel needs — run history, the posting review queue, and the curated target
 * list.
 */

import { apiClient } from './client';
import type {
  PromoteScoutPostingsRequest,
  PromoteScoutPostingsResponse,
  ReviewScoutPostingsRequest,
  ReviewScoutPostingsResponse,
  ScoutPostingDetail,
  ScoutPostingFilters,
  ScoutPostingsResponse,
  ScoutProgram,
  ScoutRunFilters,
  ScoutRunSummary,
  ScoutRunTasksResponse,
  ScoutTaskStatus,
  ScoutRunsResponse,
  ScoutTarget,
  ScoutTargetFilters,
  UpdateScoutProgramRequest,
  UpdateScoutTargetRequest,
} from '@repo/types';

export interface ScoutPagination {
  page?: number;
  limit?: number;
}

class ScoutApi {
  /** GET /api/scout/programs */
  async getPrograms(): Promise<ScoutProgram[]> {
    const response = await apiClient.get<ScoutProgram[]>('/scout/programs');
    return response.data;
  }

  /** PATCH /api/scout/programs/:id — tune a program's hiring vocabulary. */
  async updateProgram(id: string, payload: UpdateScoutProgramRequest): Promise<ScoutProgram> {
    const response = await apiClient.patch<ScoutProgram>(`/scout/programs/${id}`, payload);
    return response.data;
  }

  /**
   * PATCH /api/scout/targets/:targetId
   * Correct the company name a target was seeded or resolved with.
   */
  async updateTarget(id: string, payload: UpdateScoutTargetRequest): Promise<ScoutTarget> {
    const response = await apiClient.patch<ScoutTarget>(`/scout/targets/${id}`, payload);
    return response.data;
  }

  /** GET /api/scout/targets */
  async getTargets(filters: ScoutTargetFilters = {}): Promise<ScoutTarget[]> {
    const params = new URLSearchParams();
    if (filters.programIds?.length) params.append('programIds', filters.programIds.join(','));
    if (filters.ats) params.append('ats', filters.ats);
    if (filters.active !== undefined) params.append('active', String(filters.active));
    if (filters.search) params.append('search', filters.search);

    const query = params.toString();
    const response = await apiClient.get<ScoutTarget[]>(
      query ? `/scout/targets?${query}` : '/scout/targets'
    );
    return response.data;
  }

  /** GET /api/scout/runs */
  async getRuns(
    filters: ScoutRunFilters = {},
    pagination: ScoutPagination = {}
  ): Promise<ScoutRunsResponse> {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.employeeId) params.append('employeeId', filters.employeeId);
    // Archived runs are hidden unless asked for, and `true` returns ONLY them.
    if (filters.archived) params.append('archived', 'true');
    if (pagination.page) params.append('page', String(pagination.page));
    if (pagination.limit) params.append('limit', String(pagination.limit));

    const response = await apiClient.get<ScoutRunsResponse>(`/scout/runs?${params.toString()}`);
    return response.data;
  }

  /**
   * PATCH /api/scout/runs/:id/archive
   * Hide a run from the list, or bring it back. Destroys nothing.
   */
  async archiveRun(id: string, archived: boolean): Promise<ScoutRunSummary> {
    const response = await apiClient.patch<ScoutRunSummary>(`/scout/runs/${id}/archive`, {
      archived,
    });
    return response.data;
  }

  /**
   * DELETE /api/scout/runs/:id
   *
   * Cascades to the run's site results and postings. The server refuses a
   * running run, and refuses any run holding promoted postings — those can only
   * be archived, since their `job_scraping_queue` rows survive and would be left
   * with no record of where they came from.
   */
  async deleteRun(id: string): Promise<{ postings: number; tasks: number }> {
    const response = await apiClient.delete<{ postings: number; tasks: number }>(
      `/scout/runs/${id}`
    );
    return response.data;
  }

  /** GET /api/scout/runs/:id */
  async getRun(id: string): Promise<ScoutRunSummary> {
    const response = await apiClient.get<ScoutRunSummary>(`/scout/runs/${id}`);
    return response.data;
  }

  /**
   * GET /api/scout/runs/:runId/tasks
   * The per-site breakdown behind a run's counts. `status: 'failed'` asks only
   * for the sites that broke, which is what the run sheet opens on.
   */
  async getRunTasks(id: string, status?: ScoutTaskStatus): Promise<ScoutRunTasksResponse> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    const response = await apiClient.get<ScoutRunTasksResponse>(
      `/scout/runs/${id}/tasks?${params.toString()}`
    );
    return response.data;
  }

  /** GET /api/scout/postings */
  async getPostings(
    filters: ScoutPostingFilters = {},
    pagination: ScoutPagination = {}
  ): Promise<ScoutPostingsResponse> {
    const params = new URLSearchParams();
    if (filters.runId) params.append('runId', filters.runId);
    if (filters.targetId) params.append('targetId', filters.targetId);
    if (filters.programId) params.append('programId', filters.programId);
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    if (pagination.page) params.append('page', String(pagination.page));
    if (pagination.limit) params.append('limit', String(pagination.limit));

    const response = await apiClient.get<ScoutPostingsResponse>(
      `/scout/postings?${params.toString()}`
    );
    return response.data;
  }

  /** GET /api/scout/postings/:id — full detail, including the raw agent output. */
  async getPosting(id: string): Promise<ScoutPostingDetail> {
    const response = await apiClient.get<ScoutPostingDetail>(`/scout/postings/${id}`);
    return response.data;
  }

  /** POST /api/scout/postings/review — approve or reject. */
  async reviewPostings(
    payload: ReviewScoutPostingsRequest
  ): Promise<ReviewScoutPostingsResponse> {
    const response = await apiClient.post<ReviewScoutPostingsResponse>(
      '/scout/postings/review',
      payload
    );
    return response.data;
  }

  /**
   * POST /api/scout/postings/promote — hand approved postings to the fetcher.
   * Writes their URLs into `job_scraping_queue`; idempotent, so a double-click
   * cannot double-enqueue.
   */
  async promotePostings(
    payload: PromoteScoutPostingsRequest
  ): Promise<PromoteScoutPostingsResponse> {
    const response = await apiClient.post<PromoteScoutPostingsResponse>(
      '/scout/postings/promote',
      payload
    );
    return response.data;
  }
}

export const scoutApi = new ScoutApi();
